// 控制端信令：中央 server（账号）或局域网直连（常设密码），重连与心跳。
import type { ControllerSession } from '../controller';
import type { ElectronApi } from '../../shared/electron-api';
import { CENTRAL_SERVER_URL, DEFAULT_CLIENT_PORT, toWebSocketUrl } from '../../shared/constants';
import { DEFAULT_ICE_SERVERS } from '../../shared/ice';
import { detectBrowserInputMode, detectBrowserPlatform, normalizePlatform, platformLabel } from '../../shared/platform';
import { pendingWebRtcPath, disconnectedWebRtcPath } from '../../shared/rtc-path';
import { normalizeSessionAccessMode, type SessionAccessMode } from '../../shared/session-mode';
import { classifyConnectionFailure } from '../../shared/connection-state';
import { restoreGuestPairPassword } from '../../shared/pairing-reconnect';

// 1.2s + 2.4s + 4.8s + 9.6s + 8×10s ≈ 98s。
// 给网络切换、电脑唤醒和 Wi‑Fi 重新获取地址足够时间，同时仍会在约两分钟
// 后交给用户明确重试，不把长期离线伪装成无限加载。
const MAX_AUTO_RECONNECT_ATTEMPTS = 12;

function createSessionId(): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  } catch {
    // Fall back for older Electron/WebView runtimes without randomUUID.
  }
  return `session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

type ElectronApiWithLocalDeviceId = ElectronApi & {
  getLocalDeviceId?: () => Promise<string | null>;
};

type ElectronApiWithServerUrl = ElectronApi & {
  // MobileApi keeps the active browser origin here. The real Electron API
  // intentionally has no mutable URL property and falls back to the product
  // central service below.
  serverUrl?: string;
};

export const controllerSignaling = {
  connectGuest(this: ControllerSession, code: string, password: string, controllerKey: string, controllerDeviceId?: string, accessMode: SessionAccessMode = 'control'): void {
    this.dispose();
    this.resetConnectionTracking();
    this.accessMode = normalizeSessionAccessMode(accessMode);
    this.guestPassword = password || null;
    const apiWithServerUrl = this.api as ElectronApiWithServerUrl;
    this.open(toWebSocketUrl(apiWithServerUrl.serverUrl || CENTRAL_SERVER_URL), {
      type: 'guest-pair', code, password, controllerKey, controllerDeviceId,
      accessMode: this.accessMode,
      ...this.controllerIdentity(),
    });
  },
  // 账号模式：经中央 server 配对，免密
  async connectCentral(this: ControllerSession, token: string, targetDeviceId: string, accessMode: SessionAccessMode = 'control'): Promise<void> {
    this.dispose();
    this.resetConnectionTracking();
    this.accessMode = normalizeSessionAccessMode(accessMode);
    // 获取本机 ID 用于服务端的最终自连接校验。Web 端 HTTPS 下无法访问
    // 本机 Client，此时保留服务端已有的账号/设备校验，不阻断正常连接。
    this.disposed = false;
    const generation = this.openGeneration;
    const controllerDeviceId = await this.getControllerDeviceId();
    if (this.disposed || generation !== this.openGeneration) return;
    const apiWithServerUrl = this.api as ElectronApiWithServerUrl;
    const wsUrl = toWebSocketUrl(apiWithServerUrl.serverUrl || CENTRAL_SERVER_URL);
    this.open(
      wsUrl,
      {
        type: 'client-pair',
        accountToken: token,
        targetDeviceId,
        ...(controllerDeviceId ? { controllerDeviceId } : {}),
        accessMode: this.accessMode,
        ...this.controllerIdentity(),
      },
      true,
    );
  },

  // 局域网直连：被控端内置信令 + 常设密码
  async connectLan(this: ControllerSession, addr: string, lanPasscode: string, accessMode: SessionAccessMode = 'control'): Promise<void> {
    this.dispose();
    this.resetConnectionTracking();
    this.accessMode = normalizeSessionAccessMode(accessMode);
    this.disposed = false;
    const generation = this.openGeneration;
    const controllerDeviceId = await this.getControllerDeviceId();
    if (this.disposed || generation !== this.openGeneration) return;
    const wsUrl = this.toWsUrl(addr);
    this.open(wsUrl, {
      type: 'client-pair',
      lanPasscode,
      ...(controllerDeviceId ? { controllerDeviceId } : {}),
      accessMode: this.accessMode,
      ...this.controllerIdentity(),
    });
  },

  controllerIdentity(this: ControllerSession): {
    controllerPlatform: string;
    controllerName: string;
    controllerInput: 'pointer' | 'touch';
  } {
    return {
      controllerPlatform: detectBrowserPlatform(),
      controllerName: `${platformLabel(normalizePlatform(detectBrowserPlatform()))} ${'api' in window ? '客户端' : '浏览器'}`,
      controllerInput: detectBrowserInputMode(),
    };
  },

  async getControllerDeviceId(this: ControllerSession): Promise<string | undefined> {
    const candidate = this.api as ElectronApiWithLocalDeviceId;
    try {
      if (typeof candidate.getDeviceInfo === 'function') {
        const info = await candidate.getDeviceInfo();
        if (info?.deviceId) return info.deviceId;
      }
    } catch {
      // Web 端没有 Electron IPC 时继续尝试本机 WebSocket 探测。
    }
    try {
      if (typeof candidate.getLocalDeviceId === 'function') {
        const id = await candidate.getLocalDeviceId();
        return id || undefined;
      }
    } catch {
      // 本机探测失败不应阻断正常的远程连接。
    }
    return undefined;
  },

  open(this: ControllerSession, wsUrl: string, pairMsg: unknown, fetchIce = false, preserveFileTransfer = false): void {
    const generation = ++this.openGeneration;
    this.closePeerConnection(preserveFileTransfer);
    const oldWs = this.ws;
    this.ws = null;
    this.stopHeartbeat();
    this.releasePair(oldWs, 'controller-switch');
    try {
      oldWs?.close();
    } catch {
      // ignore
    }
    this.disposed = false;
    this.accessMode = normalizeSessionAccessMode((pairMsg as { accessMode?: unknown } | null)?.accessMode);
    const sessionId = createSessionId();
    const enrichedPairMsg = pairMsg && typeof pairMsg === 'object' && !Array.isArray(pairMsg)
      ? { ...(pairMsg as Record<string, unknown>), sessionId }
      : pairMsg;
    this.sessionId = sessionId;
    this.remotePeer = null;
    this.streamReady = false;
    this.firstFrameMs = null;
    this.cb.onFirstFrame?.(null);
    this.controlReady = false;
    this.remoteInputReady = null;
    this.cb.onControlReady?.(false);
    this.lastPathSignature = '';
    this.cb.onPeerInfo?.(null);
    this.cb.onConnectionPath?.(pendingWebRtcPath());
    this.currentWsUrl = wsUrl;
    this.currentPairMsg = enrichedPairMsg;
    // 每次重新打开信令/RTC 都是一个新的底层连接周期。保留整个用户会话的
    // 状态轨迹和 reconnectCount，但首帧与 connectedAt 必须从本次周期重新计时，
    // 否则重连后的诊断会把旧连接耗时误算到新连接上。
    this.connectionStartedAt = Date.now();
    this.connectedAt = null;
    this.setConnectionState('connecting', 'signaling', '正在连接…', true);
    this.cb.onStatus('正在连接…');
    if (fetchIce) this.iceServers = null; // 账号模式重新从 server 拉取
    const ws = new WebSocket(wsUrl);
    this.ws = ws;
    ws.onopen = async () => {
      if (this.disposed || this.ws !== ws || generation !== this.openGeneration) return;
      // 控制端在渲染进程，无法忽略证书错误：wss 需 server 使用 CA 签发证书。
      // 自签证书仅主进程(central)侧可用，详见 README「公网部署」。
      if (fetchIce) {
        try {
          const r = await this.api.getIceServers();
          this.iceServers = r.iceServers.length ? r.iceServers : DEFAULT_ICE_SERVERS;
        } catch {
          // 拉取失败则回退默认 STUN
          this.iceServers = DEFAULT_ICE_SERVERS;
        }
      }
      if (this.disposed || this.ws !== ws || generation !== this.openGeneration) return;
      this.setConnectionState('connecting', 'signaling', '正在发送连接请求…', true);
      this.startHeartbeat(ws, generation);
      ws.send(JSON.stringify(enrichedPairMsg));
    };
    ws.onmessage = (ev) => {
      if (this.disposed || this.ws !== ws || generation !== this.openGeneration) return;
      try {
        this.onSignal(JSON.parse(ev.data));
      } catch {
        this.setConnectionState('recovering', 'signaling', '收到无效连接信息，正在恢复…', true);
        this.cb.onStatus('连接信息异常，正在恢复…', 'error');
        this.scheduleReconnect();
      }
    };
    ws.onclose = () => {
      if (this.disposed || this.ws !== ws || generation !== this.openGeneration) return;
      this.ws = null;
      this.stopHeartbeat();
      this.remotePeer = null;
      this.cb.onPeerInfo?.(null);
      // 信令已断开时不要继续保留旧 RTC 通道。否则控制端看似进入重连，
      // 旧通道仍可能占用资源，直到服务端清理 peer 映射。
      this.closePeerConnection(true);
      this.controlReady = false;
      this.remoteInputReady = null;
      this.cb.onControlReady?.(false);
      this.setConnectionState('recovering', 'network', '连接断开，正在恢复…', true);
      this.cb.onStatus('连接断开，重连中…', 'idle');
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      if (!this.disposed) {
        this.setConnectionState('recovering', 'network', '无法连接对方，正在重试…', true);
        this.cb.onStatus('无法连接对方，正在重试…', 'idle');
      }
    };
  },

  // 信令断开后按退避重连；账号模式重新拉取 ICE 并重配对
  scheduleReconnect(this: ControllerSession): void {
    if (this.disposed || this.reconnectTimer) return;
    if (this.reconnectAttempt >= MAX_AUTO_RECONNECT_ATTEMPTS) {
      // 无限重试会把“设备离线”伪装成一直加载，也会让用户无法判断
      // 当前会话是否仍然值得等待。交给上层显示一个可再次尝试的恢复面板。
      this.setConnectionState('failed', 'timeout', '连接没有响应，已停止自动重试。', true);
      this.dispose();
      // 保留失败状态供恢复面板和诊断读取；dispose 只负责释放底层资源。
      this.setConnectionState('failed', 'timeout', '连接没有响应，已停止自动重试。', true);
      this.cb.onReconnectExhausted?.();
      return;
    }
    const delay = Math.min(10_000, 1_200 * 2 ** Math.min(this.reconnectAttempt, 3));
    this.reconnectAttempt += 1;
    this.reconnectCount += 1;
    this.setConnectionState('recovering', 'network', `正在恢复连接（第 ${this.reconnectAttempt} 次）…`, true);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      const wsUrl = this.currentWsUrl;
      if (this.disposed || !wsUrl) return;
      const isAccount = (this.currentPairMsg as { accountToken?: string })?.accountToken != null;
      void (async () => {
        let pairMsg = this.currentPairMsg;
        if (!isAccount) pairMsg = restoreGuestPairPassword(pairMsg, this.guestPassword);
        // 账号令牌可能在后台校验时静默续期。重连前读取最新设置，避免
        // 控制端长时间挂起后仍拿旧令牌配对，导致“配对失败”。
        if (isAccount) {
          const getSettings = (this.api as unknown as {
            getSettings?: () => Promise<{ token?: string }>;
          }).getSettings;
          if (typeof getSettings === 'function') {
            try {
              const settings = await getSettings.call(this.api);
              if (settings.token && pairMsg && typeof pairMsg === 'object' && !Array.isArray(pairMsg)) {
                pairMsg = { ...(pairMsg as Record<string, unknown>), accountToken: settings.token };
                this.currentPairMsg = pairMsg;
              }
            } catch {
              // 读取本地设置失败时沿用当前会话令牌，正常的失败处理仍会生效。
            }
          }
        }
        if (this.disposed || this.currentWsUrl !== wsUrl) return;
        this.open(wsUrl, pairMsg, isAccount, true);
      })();
    }, delay);
  },

  toWsUrl(this: ControllerSession, addr: string): string {
    const trimmed = addr.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
    const host = trimmed.includes(':') ? trimmed : `${trimmed}:${DEFAULT_CLIENT_PORT}`;
    return `ws://${host}/ws`;
  },

  startHeartbeat(this: ControllerSession, ws: WebSocket, generation: number): void {
    this.stopHeartbeat();
    this.signalingHeartbeatAckAt = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (
        this.disposed ||
        this.ws !== ws ||
        generation !== this.openGeneration ||
        ws.readyState !== WebSocket.OPEN
      ) return;
      if (Date.now() - this.signalingHeartbeatAckAt > 45_000) {
        // 半开 WebSocket 不一定会触发 onclose；主动关闭它，让服务端尽快
        // 清理配对占用，随后由现有退避逻辑重连。
        try { ws.close(); } catch { /* ignore */ }
        return;
      }
      try {
        ws.send(JSON.stringify({ type: 'heartbeat', sessionId: this.sessionId, ts: Date.now() }));
      } catch {
        // close/error handlers will schedule the reconnect.
      }
    }, 15_000);
  },

  stopHeartbeat(this: ControllerSession): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.signalingHeartbeatAckAt = 0;
  },

  // ICE candidate / SDP 都必须从当前信令连接转发到被控端。
  // 该方法曾在单体 ControllerSession 中存在，模块拆分时漏挂到原型，
  // 导致第一次配对前的 dispose() 或首个 candidate 到达时直接运行时异常。
  sendSignal(this: ControllerSession, data: unknown): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      if (!this.disposed) this.scheduleReconnect();
      return;
    }
    try {
      ws.send(JSON.stringify({ type: 'signal', sessionId: this.sessionId, data }));
    } catch {
      if (!this.disposed) this.scheduleReconnect();
    }
  },

  releasePair(this: ControllerSession, ws: WebSocket | null = this.ws, reason = 'controller-disconnect'): void {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: 'pair-release', sessionId: this.sessionId, reason: reason.slice(0, 64) }));
    } catch {
      // WebSocket close 仍会作为最终兜底清理服务端配对。
    }
  },

  // 结束控制会话时必须同时取消重连、关闭 RTC 和信令连接。
  // 这是 connectCentral/connectLan 的前置清理，也是用户点击“断开”时的
  // 最终清理入口；不能只依赖页面切换，否则旧连接会继续抢占配对。
  dispose(this: ControllerSession): void {
    const wasActive = !this.disposed || Boolean(this.currentWsUrl || this.ws || this.pc);
    this.disposed = true;
    this.openGeneration += 1;
    this.reconnectAttempt = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    this.closePeerConnection();

    const ws = this.ws;
    this.ws = null;
    this.releasePair(ws);
    try {
      ws?.close();
    } catch {
      // ignore
    }

    this.currentWsUrl = '';
    this.currentPairMsg = null;
    this.guestPassword = null;
    this.sessionId = null;
    this.remotePeer = null;
    this.controlReady = false;
    this.remoteInputReady = null;
    this.stopControlHeartbeat();
    if (wasActive) this.setConnectionState('closed', 'manual', '连接已结束', false);
  },

  onSignal(this: ControllerSession, msg: {
    type: string;
    deviceId?: string;
    platform?: string;
    hostname?: string;
    deviceName?: string;
    sessionId?: string;
    accessMode?: SessionAccessMode;
    reason?: string;
    data?: unknown;
    message?: string;
    ts?: number;
    iceServers?: RTCIceServer[];
  }): void {
    if (msg.sessionId && this.sessionId && msg.sessionId !== this.sessionId) return;
    if (msg.type === 'heartbeat-ack') {
      this.signalingHeartbeatAckAt = Date.now();
      return;
    }
    switch (msg.type) {
      case 'guest-pending':
        this.setConnectionState('awaiting-approval', 'approval', '等待对方允许…', true);
        this.cb.onStatus('等待对方允许… 可随时取消');
        break;
      case 'pair-ok':
        if (msg.sessionId) this.sessionId = msg.sessionId;
        this.reconnectAttempt = 0;
        this.accessMode = normalizeSessionAccessMode(
          msg.accessMode ?? (this.currentPairMsg as { accessMode?: unknown } | null)?.accessMode,
        );
        this.startPairWaitTimer();
        if (msg.iceServers?.length) this.iceServers = msg.iceServers;
        if ((this.currentPairMsg as { type?: string })?.type === 'guest-pair') delete (this.currentPairMsg as { password?: string }).password;
        this.remotePeer = {
          deviceId: msg.deviceId || '',
          platform: msg.platform || 'unknown',
          os: normalizePlatform(msg.platform),
          hostname: msg.hostname || 'unknown',
          deviceName: msg.deviceName || msg.hostname || '远端设备',
        };
        this.cb.onDevice(
          `${this.remotePeer.deviceName} · ${platformLabel(this.remotePeer.os)}`,
        );
        this.cb.onPeerInfo?.(this.remotePeer);
        this.setConnectionState('paired', 'pairing', '已配对，正在等待画面…', true);
        this.cb.onStatus('已配对，等待画面…', 'online');
        break;
      case 'pair-fail':
        this.stopPairWaitTimer();
        this.closePeerConnection(true);
        const failure = classifyConnectionFailure(msg.reason);
        const pairType = (this.currentPairMsg as { type?: string })?.type;
        const retryable = pairType !== 'guest-pair' &&
          (failure.reason === 'offline' || failure.reason === 'busy');
        if (!retryable) {
          // 配对失败也要释放信令 socket。否则服务端可能继续保留旧的
          // 控制端占用，下一次连接就会被误判为“设备正在使用中”。
          this.dispose();
          this.setConnectionState('failed', failure.reason, failure.message, failure.retryable);
          this.cb.onStatus(failure.message, 'error');
          break;
        }
        this.cb.onStatus(failure.message, 'error');
        // 目标设备短暂掉线或服务端仍在清理旧 socket 时自动重试；
        // 密码/权限错误则停在当前提示，避免错误凭据触发无限重连。
        const oldWs = this.ws;
        this.ws = null;
        this.stopHeartbeat();
        try { oldWs?.close(); } catch { /* ignore */ }
        this.cb.onStatus(
          failure.reason === 'busy'
            ? '旧连接正在释放，正在重试…'
            : '目标设备暂时离线，正在重试…',
          'idle',
        );
        this.scheduleReconnect();
        break;
      case 'peer-error':
        this.stopPairWaitTimer();
        this.closePeerConnection();
        this.controlReady = false;
        this.remoteInputReady = null;
        this.cb.onControlReady?.(false);
        this.cb.onConnectionPath?.(disconnectedWebRtcPath());
        const peerFailure = classifyConnectionFailure(msg.reason);
        // 被控端已经明确报告本次会话无法继续。关闭旧信令连接，避免
        // 目标设备保持占用；可恢复错误由上层恢复面板重新创建会话。
        this.dispose();
        this.setConnectionState('failed', peerFailure.reason, peerFailure.message, peerFailure.retryable);
        this.cb.onStatus(peerFailure.message, 'error');
        break;
      case 'signal':
        void this.onWebRtcSignal(
          msg.data as { sdp?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit },
        );
        break;
      case 'peer-left':
        this.remotePeer = null;
        this.cb.onPeerInfo?.(null);
        // 对端的信令连接可能只是暂时重启。释放旧 RTC 通道但保留配对
        // 参数，重新打开信令后可自动恢复，而不是要求用户重新操作。
        this.closePeerConnection(true);
        this.controlReady = false;
        this.remoteInputReady = null;
        this.cb.onControlReady?.(false);
        this.cb.onRemoteCursorMode?.('overlay');
        this.cb.onRemoteCursor?.({ type: 'cursor', visible: false });
        this.cb.onConnectionPath?.(disconnectedWebRtcPath());
        this.setConnectionState('recovering', 'remote-ended', '对方暂时断开，正在恢复…', true);
        this.cb.onStatus('对方暂时断开，正在恢复…', 'idle');
        this.scheduleReconnect();
        break;
      case 'error':
        const errorFailure = classifyConnectionFailure(msg.message);
        this.dispose();
        this.setConnectionState('failed', errorFailure.reason, errorFailure.message, errorFailure.retryable);
        this.cb.onStatus(errorFailure.message, 'error');
        break;
    }
  },
};
